/**
 * Admin User Editor (AR-327).
 *
 * One member record page: a persistent header and right rail wrapping four
 * tabs — Profile, Account + Team, Sales Data, Member Activation — so an admin
 * can serve a member without moving between systems.
 *
 * Scope: layout plus reads. Fields bind to `GET /api/admin/users/:id/record`;
 * saving and the workflows behind each admin action ship with their companion
 * backend tickets, so every trigger point here confirms and then reports the
 * deferral rather than calling anything.
 */
import React, { useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, ArrowLeft, Copy } from 'lucide-react';
import { format } from 'date-fns';
import { RoleAccessSheet } from '@/components/admin/RoleAccessSheet';
import {
  DesignScopeNotice,
  StatePill,
} from '@/components/admin/user-record/recordPrimitives';
import { ProfileTab, roleLabel } from '@/components/admin/user-record/ProfileTab';
import { AccountTeamTab } from '@/components/admin/user-record/AccountTeamTab';
import { SalesDataTab } from '@/components/admin/user-record/SalesDataTab';
import { MemberActivationTab } from '@/components/admin/user-record/MemberActivationTab';
import { RecordSidebar } from '@/components/admin/user-record/RecordSidebar';
import { accountStatusLabel, useRecordDraft } from '@/components/admin/user-record/useRecordDraft';
import {
  EM_DASH,
  useAdminUserRecord,
  useSaveAdminUserRecord,
} from '@/lib/adminUserRecordApi';

const formatDate = (value: string | null | undefined): string => {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? EM_DASH : format(parsed, 'MMM d, yyyy');
};

function CopyValue({ label, value }: { label: string; value: string | null }) {
  const { toast } = useToast();
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      {value}
      <button
        type="button"
        aria-label={`Copy ${label}`}
        className="rounded p-0.5 text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:hover:text-slate-200"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            toast({ title: `${label} copied` });
          } catch {
            toast({ title: `Could not copy ${label}`, variant: 'destructive' });
          }
        }}
      >
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

function QuickStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 whitespace-nowrap text-sm font-semibold text-slate-900 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
}

export default function AdminUserRecordPage() {
  const [, params] = useRoute('/admin/users/:id');
  const [, navigate] = useLocation();
  const userId = params?.id ? Number(params.id) : null;
  const { data: record, isLoading, isError, error, refetch } = useAdminUserRecord(userId);
  const draft = useRecordDraft(record);
  const [roleSheetOpen, setRoleSheetOpen] = useState(false);
  const save = useSaveAdminUserRecord(userId ?? 0);
  const { toast } = useToast();

  const onSave = () => {
    const fields = draft.changes();
    if (Object.keys(fields).length === 0) {
      draft.reset();
      return;
    }
    save.mutate(fields, {
      onSuccess: (result) => {
        draft.reset();
        toast({ title: `Saved ${result.saved} field${result.saved === 1 ? '' : 's'}` });
      },
      onError: (err) =>
        toast({
          title: 'Could not save the member record',
          description: (err as Error).message,
          variant: 'destructive',
        }),
    });
  };

  const backToList = (
    <Button
      variant="ghost"
      size="sm"
      className="text-slate-600 dark:text-slate-300"
      onClick={() => navigate('/admin/users')}
    >
      <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden="true" />
      Return to User Management
    </Button>
  );

  if (isLoading) {
    return (
      <div className="min-h-full bg-slate-50 p-6 dark:bg-gray-950">
        {backToList}
        <Skeleton className="mt-4 h-28 w-full rounded-xl" />
        <Skeleton className="mt-5 h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (isError || !record) {
    return (
      <div className="min-h-full bg-slate-50 p-6 dark:bg-gray-950">
        {backToList}
        <div className="mt-6 flex max-w-xl items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-5 dark:border-rose-900 dark:bg-rose-950">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" aria-hidden="true" />
          <div>
            <p className="font-semibold text-rose-900 dark:text-rose-200">
              Could not load this member record
            </p>
            <p className="mt-1 text-sm text-rose-800 dark:text-rose-300">
              {(error as Error | null)?.message ?? 'The record is unavailable right now.'}
            </p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { user, profile, team } = record;
  const companyName = team?.brandName ?? team?.name ?? profile?.businessName ?? null;
  const planName = record.plan?.name ?? user.subscriptionTier;
  const status = accountStatusLabel(record);

  return (
    <div className="min-h-full bg-slate-50 dark:bg-gray-950">
      <div className="border-b border-slate-200 bg-white px-6 py-3 dark:border-gray-700 dark:bg-gray-800">
        {backToList}
      </div>

      <header className="border-b border-slate-200 bg-white px-6 py-6 dark:border-gray-700 dark:bg-gray-800">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600 dark:text-orange-400">
          Admin / User Management
        </p>
        <div className="mt-3 flex flex-wrap items-start gap-5">
          <Avatar className="h-16 w-16 rounded-xl">
            <AvatarImage src={user.avatarUrl ?? undefined} alt={user.displayName} />
            <AvatarFallback className="rounded-xl text-lg">
              {user.displayName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-[260px] flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {user.displayName}
              </h1>
              <StatePill tone={status === 'Active' ? 'dark' : 'warning'}>{status}</StatePill>
              <StatePill tone="neutral">{planName}</StatePill>
              <StatePill tone="accent">{roleLabel(user.roleSlug ?? user.role)}</StatePill>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
              <CopyValue label="Email" value={user.email} />
              {companyName && <span>· {companyName}</span>}
              {(profile?.phone ?? user.phone) && (
                <span>
                  ·&nbsp;
                  <CopyValue label="Phone" value={profile?.phone ?? user.phone} />
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
            <QuickStat
              label="Member Since"
              value={formatDate(profile?.memberSince ?? user.createdAt)}
            />
            <QuickStat
              label="Last Verified"
              value={formatDate(
                record.migrated ? record.migrated.user.lastVerifiedAt : profile?.worldviaLastSyncedAt,
              )}
            />
            <QuickStat label="GEM Status" value={profile?.gemStatus || EM_DASH} />
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[1600px] flex-col items-start gap-6 px-6 py-6 2xl:flex-row">
        <div className="w-full min-w-0 flex-1">
          <DesignScopeNotice bound={draft.bound} unbound={record.migrated?.unbound} />

          <Tabs defaultValue="profile" className="mt-4">
            <TabsList className="grid h-auto w-full grid-cols-2 gap-1 bg-white p-1.5 sm:grid-cols-4 dark:bg-gray-800">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="account">Account + Team</TabsTrigger>
              <TabsTrigger value="sales">Sales Data</TabsTrigger>
              <TabsTrigger value="activation">Member Activation</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="mt-5">
              <ProfileTab
                record={record}
                draft={draft}
                onManageAccess={() => setRoleSheetOpen(true)}
              />
            </TabsContent>
            <TabsContent value="account" className="mt-5">
              <AccountTeamTab record={record} draft={draft} />
            </TabsContent>
            <TabsContent value="sales" className="mt-5">
              <SalesDataTab gemStatus={profile?.gemStatus ?? null} sales={record.migrated?.sales} />
            </TabsContent>
            <TabsContent value="activation" className="mt-5">
              <MemberActivationTab
                onboardingStatus={record.onboarding?.status ?? user.onboardingStatus}
                routeName={record.onboarding?.routeName ?? null}
                pathwayName={record.onboarding?.pathwayName ?? null}
              />
            </TabsContent>
          </Tabs>
        </div>

        <RecordSidebar record={record} />
      </div>

      <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-3 dark:border-gray-700 dark:bg-gray-800">
        <p className="mr-auto text-xs text-slate-500 dark:text-slate-400">
          {draft.dirty
            ? draft.bound
              ? 'Unsaved edits.'
              : 'Unsaved edits — saving lands with the companion backend ticket.'
            : 'No edits yet.'}
        </p>
        <Button variant="outline" disabled={!draft.dirty || save.isPending} onClick={draft.reset}>
          Discard Changes
        </Button>
        {draft.bound ? (
          <Button disabled={!draft.dirty || save.isPending} onClick={onSave}>
            {save.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        ) : (
          <Button disabled title="Saving ships with the companion backend ticket">
            Save Changes
          </Button>
        )}
      </div>

      <RoleAccessSheet
        userId={user.id}
        userName={user.displayName}
        userEmail={user.email}
        open={roleSheetOpen}
        onOpenChange={setRoleSheetOpen}
        onSuccess={() => void refetch()}
      />
    </div>
  );
}
